import { track } from "@/lib/analytics";
import { trackRokuInitiateCheckout } from "@/lib/roku";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState } from "react";
import { Check, Sparkles, Zap, ArrowLeft, Crown, Infinity, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { CancelSubscriptionDialog } from "@/components/CancelSubscriptionDialog";
import { getPaymentEnvironment } from "@/lib/stripe";
import {
  createBillingPortalSession,
  changeSubscriptionPlan,
  createCheckoutSession,
} from "@/utils/payments.functions";
import { toast } from "sonner";
import { trackBillingFunnel } from "@/lib/billing-funnel";
import { useTranslation } from "react-i18next";
import { RefreshCw, Loader2 } from "lucide-react";
import {
  canFallBackToWebCheckout,
  isStoreManagedBilling,
  mustUseStoreBilling,
  nativePlatform,
  storeLabel,
  storeLog,
  storeProductId,
  PRODUCT_DISPLAY_NAMES,
} from "@/lib/store-billing";
import { purchasePremiumViaStore, restoreStorePurchases, openStoreManagement } from "@/lib/native-billing";
import { useStorePrices } from "@/hooks/useStorePrices";
import { StoreSubscriptionTerms } from "@/components/StoreSubscriptionTerms";

import { T } from "@/components/T";
import i18n from "@/i18n";
import { STUDY_TERMS } from "@/lib/study-policy";
export const Route = createFileRoute("/upgrade")({
  component: UpgradePage,
  head: () => ({
    meta: [
      { title: "Upgrade to Inquira Pro" },
      { name: "description", content: "Compare Inquira Premium study access and the proposed 50-question daily allowance." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { property: "og:title", content: "Upgrade to Inquira Pro — $9.99/mo" },
      { property: "og:description", content: "Premium study tools, guided explanations, and transparent daily allowances." },
      { property: "og:url", content: "https://www.inquira.online/upgrade" },
      { name: "twitter:title", content: "Upgrade to Inquira Pro" },
      { name: "twitter:description", content: "Premium study features for $9.99/month. Existing benefits are preserved." },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/upgrade" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: "Can I cancel anytime?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes! Cancel your subscription anytime with no questions asked. You'll keep Pro access until the end of your billing period.",
              },
            },
            {
              "@type": "Question",
              name: "What subjects are supported?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Inquira works with math (algebra, calculus, statistics), physics, chemistry, and word problems. We're always adding more!",
              },
            },
            {
              "@type": "Question",
              name: "Is my data private?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Absolutely. Your problems and solutions are stored securely and only visible to you.",
              },
            },
          ],
        }),
      },
    ],
  }),
});

function UpgradePage() {
  useEffect(() => {
    trackBillingFunnel("billing_view");
  }, []);
  const { t } = useTranslation();
  const { user, isPaid, cancelAtPeriodEnd, currentPeriodEnd, planPriceId, subscriptionStatus, refreshUsage } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [showCancel, setShowCancel] = useState(false);
  const [planLoading, setPlanLoading] = useState(false);
  const [billing, setBilling] = useState<"monthly" | "yearly">("monthly");
  const priceId = billing === "yearly" ? "inquira_pro_yearly" : "inquira_pro_monthly";
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  // Inside the iOS/Android wrappers digital subscriptions MUST use the store.
  const store = mustUseStoreBilling();
  // Only treat billing as store-managed when a store really owns it, otherwise
  // the Stripe cancel / plan-switch controls would be hidden with no substitute.
  const storeManaged = isStoreManagedBilling();
  const platform = nativePlatform();
  const storePrices = useStorePrices();
  const monthlyPrice = storePrices.monthly ?? "$9.99";
  const yearlyPrice = "$79.99";
  const shownPrice = billing === "yearly" ? yearlyPrice : monthlyPrice;
  // On iOS the purchase must not be offered until StoreKit actually returned the
  // product — otherwise the tap ends in a store error and Apple forbids an
  // external checkout. Where a web (Stripe) checkout is still allowed, keep
  // Subscribe enabled: purchaseInStore() falls back to card checkout.
  const storeProductMissing =
    store &&
    !storePrices.loading &&
    !canFallBackToWebCheckout() &&
    !(billing === "yearly" ? storePrices.yearly : storePrices.monthly);


  const startWebCheckout = async () => {
    if (!user) {
      window.location.href = "/login";
      return;
    }
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      void track("checkout_started", { price_id: priceId, billing });
      const result = await createCheckoutSession({
        data: { priceId, origin: window.location.origin },
      });
      if (!result?.url) {
        setCheckoutError("Couldn't start checkout. Please try again.");
        void track("checkout_failed", { price_id: priceId, reason: "no_url" });
        return;
      }
      // Roku: a real checkout session exists — not just a button click.
      trackRokuInitiateCheckout(billing === "yearly" ? 99.99 : 9.99, "USD");
      window.location.href = result.url;
    } catch (err) {
      console.error("Checkout failed:", err);
      setCheckoutError("Couldn't start checkout. Please try again.");
      void track("checkout_failed", { price_id: priceId, reason: "exception" });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const purchaseInStore = async () => {
    if (!user) {
      window.location.href = "/login";
      return;
    }
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      void track("checkout_started", { surface: "upgrade_native", product_id: storeProductId(billing) });
      const result = await purchasePremiumViaStore(billing);
      if (!result.ok) {
        if (result.error === "cancelled") return;
        // No billing bridge in this wrapper build: outside iOS we must still
        // give the customer a way to pay, so fall back to web checkout.
        if (
          (result.error === "no_bridge" || result.error === "store_not_configured") &&
          canFallBackToWebCheckout()
        ) {
          void track("checkout_fallback_web", { surface: "upgrade_native", reason: result.error });
          setCheckoutLoading(false);
          await startWebCheckout();
          return;
        }
        setCheckoutError(
          result.error === "pending"
            ? "Your purchase is pending approval (for example Ask to Buy). Premium unlocks as soon as it is approved."
            : result.error === "product_unavailable" || result.error === "no_bridge"
              ? `We couldn't load ${PRODUCT_DISPLAY_NAMES[billing]} from the ${storeLabel(platform)}. Please check your connection and try again.`
              : result.error === "already_owned"
                ? "This subscription is already active on your Apple ID — tap Restore Purchases."
                : "The purchase couldn't be completed. Please try again.",
        );
        void track("checkout_failed", { surface: "upgrade_native", reason: result.error });
        return;
      }
      await refreshUsage();
      toast.success(t("store.premiumActive"));
    } catch (err) {
      storeLog("purchase_exception", { plan: billing });
      console.error("Store purchase failed:", err);
      setCheckoutError("The purchase couldn't be completed. Please try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const restorePurchases = async () => {
    setRestoreLoading(true);
    setCheckoutError(null);
    try {
      const result = await restoreStorePurchases();
      await refreshUsage();
      if (result.premium) toast.success(t("store.restored"));
      else toast.info(t("store.noSubscriptionFound"));
    } catch {
      setCheckoutError("We couldn't restore your purchases. Please try again.");
    } finally {
      setRestoreLoading(false);
    }
  };

  const startCheckout = async () => {
    if (billing === "yearly") { setCheckoutError("The proposed $79.99 annual plan is not yet available."); return; }
    trackBillingFunnel("checkout_start");
    if (store) {
      await purchaseInStore();
      return;
    }
    await startWebCheckout();
  };

  const openBillingPortal = async () => {
    if (!user) return;
    if (isStoreManagedBilling()) {
      const opened = await openStoreManagement();
      if (opened) return;
      setPortalError("Manage your subscription in your device's Settings > Subscriptions.");
      return;
    }
    setPortalLoading(true);
    setPortalError(null);
    try {
      const result = await createBillingPortalSession({
        data: { environment: getPaymentEnvironment(), origin: window.location.origin },
      });
      if (!result?.url) {
        setPortalError(
          result?.error === "no_subscription"
            ? "No active billing account found for this plan yet."
            : "Couldn't open the billing portal. Please try again.",
        );
        return;
      }
      window.open(result.url, "_blank");
    } catch (err) {
      console.error("Failed to open billing portal:", err);
      setPortalError("Couldn't open the billing portal. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  };

  if (isPaid) {
    const onYearly = planPriceId === "inquira_pro_yearly";
    const canSwitch = planPriceId === "inquira_pro_yearly" || planPriceId === "inquira_pro_monthly";
    const targetPlan = onYearly ? "inquira_pro_monthly" : "inquira_pro_yearly";

    const switchPlan = async () => {
      setPlanLoading(true);
      try {
        const result = await changeSubscriptionPlan({
          data: { priceId: targetPlan, environment: getPaymentEnvironment() },
        });
        if (!result?.ok) {
          toast.error(i18n.t("edu.upgrade.couldn_t_change_your_plan_please_try_aga"));
          return;
        }
        toast.success(
          result.isUpgrade
            ? "You're on the Yearly plan — the change applies right away, prorated."
            : "You'll move to the Monthly plan at your next renewal.",
        );
        await refreshUsage();
      } catch {
        toast.error(i18n.t("edu.upgrade.couldn_t_change_your_plan_please_try_aga_2"));
      } finally {
        setPlanLoading(false);
      }
    };

    return (
      <div className="min-h-screen notebook-lines">
        <PaymentTestModeBanner />
        <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink transition-colors">
            <ArrowLeft className="size-4" /> <T k="edu.upgrade.back_to_inquira" />
          </Link>
          <div className="bg-card rounded-2xl p-8 border border-rule shadow-sm text-center space-y-4">
            <div className="size-16 rounded-full bg-neon-yellow/20 flex items-center justify-center mx-auto">
              <Crown className="size-8 text-neon-pink" />
            </div>
            <h1 className="font-[family-name:var(--font-display)] font-extrabold text-2xl text-ink">
              You're on Pro! 🎉
            </h1>
            <p className="text-sm text-ink-muted">
              Your existing study benefits and full Premium access are preserved
              {canSwitch ? ` on the ${onYearly ? "Yearly" : "Monthly"} plan` : ""}.
            </p>
            {subscriptionStatus === "past_due" && (
              <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3 text-sm text-ink">
                Your last payment failed. You keep Pro access while we retry — please update your
                payment method below.
              </div>
            )}
            {cancelAtPeriodEnd && currentPeriodEnd && (
              <div className="bg-neon-yellow/10 border border-neon-yellow/30 rounded-xl p-3 text-sm text-ink">
                Your subscription is set to cancel. You'll keep Pro access until{" "}
                <span className="font-bold">{new Date(currentPeriodEnd).toLocaleDateString()}</span>.
              </div>
            )}
            <div className="space-y-2">
              <Link to="/">
                <Button variant="scan" size="full" className="font-[family-name:var(--font-display)]">
                  <Sparkles className="size-5" /> <T k="edu.upgrade.start_solving" />
                </Button>
              </Link>
              {canSwitch && onYearly && !cancelAtPeriodEnd && !storeManaged && (
                <>
                  <Button
                    variant="subtle"
                    size="full"
                    className="font-[family-name:var(--font-display)]"
                    onClick={switchPlan}
                    disabled={planLoading}
                  >
                    <Crown className="size-5" />{" "}
                    {planLoading
                      ? "Updating plan..."
                      : onYearly
                        ? "Switch to Monthly ($9.99/mo)"
                        : "Switch to Yearly ($99.99/yr — 2 months free)"}
                  </Button>
                  <p className="text-[11px] text-ink-muted">
                    {onYearly
                      ? "Downgrades take effect at your next renewal — you keep yearly access until then."
                      : "Upgrades apply immediately and are prorated against what you've already paid."}
                  </p>
                </>
              )}
              <Button
                variant="subtle"
                size="full"
                className="font-[family-name:var(--font-display)]"
                onClick={openBillingPortal}
                disabled={portalLoading}
              >
                <Settings className="size-5" />{" "}
                {portalLoading ? "Opening..." : storeManaged ? `Manage in ${storeLabel(platform)}` : "Manage Subscription"}
              </Button>
              <p className="text-[11px] text-ink-muted">
                Opens in a new tab — update your payment method, change plans, cancel, or view invoices
              </p>
              {portalError && (
                <p className="text-[11px] text-neon-pink font-semibold">{portalError}</p>
              )}
              {storeManaged && (
                <Button
                  variant="subtle"
                  size="full"
                  className="font-[family-name:var(--font-display)]"
                  onClick={restorePurchases}
                  disabled={restoreLoading}
                >
                  <RefreshCw className="size-5" /> {restoreLoading ? "Restoring…" : "Restore Purchases"}
                </Button>
              )}
              {!cancelAtPeriodEnd && user && !storeManaged && (
                <button
                  onClick={() => setShowCancel(true)}
                  className="w-full pt-1 text-xs text-ink-muted underline hover:text-destructive"
                >
                  Cancel subscription
                </button>
              )}
              <Link to="/billing" className="block text-sm text-neon-pink font-semibold underline pt-1">
                View receipts & Pro timeline
              </Link>
            </div>
          </div>
        </div>
        {showCancel && user && (
          <CancelSubscriptionDialog
            userId={user.id}
            planPriceId={planPriceId}
            onClose={() => setShowCancel(false)}
            onCanceled={() => { void refreshUsage(); }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen notebook-lines">
      <PaymentTestModeBanner />
      <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink transition-colors">
          <ArrowLeft className="size-4" /> <T k="edu.upgrade.back_to_inquira" />
        </Link>

        <div className="text-center space-y-2">
          <h1 className="font-[family-name:var(--font-display)] font-extrabold text-3xl text-ink">
            Upgrade to <span className="text-neon-pink">Pro</span>
          </h1>
          <p className="text-sm text-ink-muted">
            {STUDY_TERMS.premiumBenefit}
          </p>
        </div>

        {/* Billing cycle toggle */}
        <div className="mx-auto flex w-full max-w-xs items-center rounded-full border border-rule bg-card p-1">
          <button
            onClick={() => setBilling("monthly")}
            className={`flex-1 rounded-full px-3 py-1.5 text-sm font-semibold transition ${
              billing === "monthly" ? "bg-neon-pink text-paper" : "text-ink-muted"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBilling("yearly")}
            className={`flex-1 rounded-full px-3 py-1.5 text-sm font-semibold transition ${
              billing === "yearly" ? "bg-neon-pink text-paper" : "text-ink-muted"
            }`}
          >
            Yearly <span className="ml-1 text-[10px] opacity-80">{t("monetization.coming_soon")}</span>
          </button>
        </div>

        {/* Free plan */}
        <div className="bg-card rounded-2xl p-6 border border-rule shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-[family-name:var(--font-display)] font-bold text-lg text-ink"><T k="edu.upgrade.free" /></h2>
            <span className="text-sm font-bold text-ink-muted">$0/mo</span>
          </div>
          <ul className="space-y-2">
            <PlanFeature text="3 AI tutor questions per day under the new Free terms" />
            <PlanFeature text="Step-by-step solutions" />
            <PlanFeature text="Image & text input" />
            <PlanFeature text="Solution history" />
          </ul>
          <div className="pt-2">
            <Button variant="subtle" size="full" disabled className="font-[family-name:var(--font-display)]">
              Current Plan
            </Button>
          </div>
        </div>

        {/* Pro plan */}
        <div className="bg-card rounded-2xl p-6 border-2 border-neon-pink/30 shadow-[0_0_30px_-10px_oklch(0.7_0.25_340/0.2)] space-y-4 relative">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-neon-pink text-paper text-xs font-bold px-4 py-1 rounded-full">
            {billing === "yearly" ? "BEST VALUE" : "MOST POPULAR"}
          </div>
          <div className="flex items-center justify-between pt-1">
            <h2 className="font-[family-name:var(--font-display)] font-bold text-lg text-ink flex items-center gap-2">
              <Crown className="size-5 text-neon-pink" /> Pro {billing === "yearly" ? "Yearly" : "Monthly"}
            </h2>
            <div className="text-right">
              {billing === "yearly" ? (
                <>
                  <span className="text-2xl font-extrabold text-ink font-[family-name:var(--font-display)]">{yearlyPrice}</span>
                  <span className="text-sm text-ink-muted">/yr</span>
                  <div className="text-[11px] text-neon-pink font-semibold">{t("monetization.proposed_save_approximately_33")}</div>
                </>
              ) : (
                <>
                  <span className="text-2xl font-extrabold text-ink font-[family-name:var(--font-display)]">{monthlyPrice}</span>
                  <span className="text-sm text-ink-muted">/mo</span>
                </>
              )}
            </div>
          </div>
          <ul className="space-y-2">
            <PlanFeature text={STUDY_TERMS.premiumBenefit} highlight />
            <PlanFeature text="Priority AI processing" highlight />
            <PlanFeature text="Deeper explanations" highlight />
            <PlanFeature text="Practice mode" highlight />
            {billing === "yearly" && <PlanFeature text="Not available until billing products are verified" highlight />}
            <PlanFeature text="Step-by-step solutions" />
            <PlanFeature text="Image & text input" />
            <PlanFeature text="Solution history" />
            <PlanFeature text="Handwriting recognition" />
          </ul>
          <div className="pt-2">
            <Button
              variant="scan"
              size="full"
              className="font-[family-name:var(--font-display)]"
              onClick={startCheckout}
              disabled={billing === "yearly" || checkoutLoading || storePrices.loading || storeProductMissing}
            >
              {checkoutLoading || storePrices.loading ? (
                <Loader2 className="size-5 animate-spin" />
              ) : (
                <Zap className="size-5" />
              )}{" "}
              {billing === "yearly" ? "Annual plan coming soon" : storePrices.loading
                ? "Loading subscription…"
                : storeProductMissing
                  ? "Subscription unavailable"
                  : checkoutLoading
                    ? store
                      ? `Opening ${storeLabel(platform)}…`
                      : "Opening checkout..."
                    : `Subscribe — ${shownPrice}/mo`}
            </Button>
            {storeProductMissing && (
              <Button
                variant="subtle"
                size="full"
                className="mt-2 font-[family-name:var(--font-display)]"
                onClick={() => storePrices.reload()}
              >
                <RefreshCw className="size-5" /> <T k="edu.upgrade.retry_loading_subscription" />
              </Button>
            )}
            {(storeManaged || store) && (
              <Button
                variant="subtle"
                size="full"
                className="mt-2 font-[family-name:var(--font-display)]"
                onClick={restorePurchases}
                disabled={restoreLoading}
              >
                {restoreLoading ? <Loader2 className="size-5 animate-spin" /> : <RefreshCw className="size-5" />}{" "}
                {restoreLoading ? "Restoring…" : "Restore Purchases"}
              </Button>
            )}
            <p className="text-[11px] text-ink-muted text-center mt-2">
              Cancel anytime • Secure payment
            </p>

            {billing === "monthly" && <StoreSubscriptionTerms
              plan="monthly"
              price={storePrices.monthly}
              fallbackPrice="$9.99"
              className="mt-3"
            />}
            <p className="mt-3 text-xs text-ink-muted">{t("monetization.new_question_limits_are_staged_and_not_active_existing_subscription_")}</p>
            {storePrices.error && (
              <p className="text-[11px] text-neon-pink font-semibold text-center mt-2">{storePrices.error}</p>
            )}
            {checkoutError && (
              <p className="text-[11px] text-neon-pink font-semibold text-center mt-1">{checkoutError}</p>
            )}
          </div>
        </div>

        {/* FAQ */}
        <div className="space-y-4 pt-2">
          <h3 className="font-[family-name:var(--font-display)] font-bold text-ink text-center">
            Frequently Asked Questions
          </h3>
          <FAQ
            q="Can I cancel anytime?"
            a="Yes! Cancel your subscription anytime with no questions asked. You'll keep Pro access until the end of your billing period."
          />
          <FAQ
            q="What subjects are supported?"
            a="Inquira works with math (algebra, calculus, statistics), physics, chemistry, and word problems. We're always adding more!"
          />
          <FAQ
            q="Is my data private?"
            a="Absolutely. Your problems and solutions are stored securely and only visible to you."
          />
        </div>

        <div className="pt-4 text-center text-xs text-ink-muted">
          Explore our{" "}
          <Link to="/calculus-ai-solver" className="text-neon-pink font-semibold underline">
            Calculus AI Solver guide
          </Link>{" "}
          to see step-by-step solutions in action.
        </div>
      </div>
    </div>
  );
}

function PlanFeature({ text, highlight }: { text: string; highlight?: boolean }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      <div className={`size-5 rounded-full flex items-center justify-center shrink-0 ${highlight ? "bg-neon-pink/10" : "bg-rule/50"}`}>
        {highlight ? <Infinity className="size-3 text-neon-pink" /> : <Check className="size-3 text-ink-muted" />}
      </div>
      <span className={highlight ? "font-semibold text-ink" : "text-ink-muted"}>{text}</span>
    </li>
  );
}

function FAQ({ q, a }: { q: string; a: string }) {
  return (
    <div className="bg-card rounded-xl p-4 border border-rule space-y-1">
      <p className="text-sm font-semibold text-ink">{q}</p>
      <p className="text-xs text-ink-muted leading-relaxed">{a}</p>
    </div>
  );
}
